import { useCallback, type ChangeEvent, type FormEvent } from 'react'
import { useIntl } from 'react-intl'

import { cn, TextField } from '@/shared/ui'
import { Search } from '@/shared/ui/icons'

/**
 * The free-text search across a screen's records, from the mockups' `HeaderSearchField`: a
 * compact search field in its own `search` landmark.
 */
export function SearchField(props: {
  readonly value: string
  readonly onChange: (value: string) => void
  readonly placeholder: string
  /** Sizes the whole field, as a toolbar's flex item; `w-56` by default. */
  readonly className?: string
}) {
  const intl = useIntl()
  const { onChange } = props
  const change = useCallback((event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value), [onChange])
  // The search runs as the text changes, so Enter has nothing more to do.
  const submit = useCallback((event: FormEvent) => event.preventDefault(), [])

  return (
    <search className={cn('min-w-0 max-w-full', props.className ?? 'w-56')}>
      <form onSubmit={submit}>
        <TextField
          label={intl.formatMessage({ id: 'search.field.label' })}
          hideLabel
          icon={Search}
          type="search"
          size="compact"
          value={props.value}
          onChange={change}
          placeholder={props.placeholder}
          className="w-full"
        />
      </form>
    </search>
  )
}
